import { Controller as BaseController } from 'stimulus'
import { useDispatch } from 'stimulus-use'

export class Controller extends BaseController {
  static targets = [
    'item'
  ]

  connect() {
    this.element[this.identifier] = this

    useDispatch(this)

    this.discoverListStructure()
    this.resolveSelectionStateForInnerItems()
    this.dispatch('loaded')
  }

  discoverListStructure() {
    this.topLevelItems = this.itemTargets.filter((item) =>
      JSON.parse(item.getAttribute('data-is-top-level'))
    )
    this.childrenCache = {}

    const discoverChildren = (item) => {
      const children = document.querySelectorAll(`input[type="checkbox"][data-parent-id="${item.id}"]`)

      this.childrenCache[item.id] = children
      children.forEach(discoverChildren)
    }

    this.topLevelItems.forEach(discoverChildren)
  }

  childrenOf(itemCheckbox) {
    return this.childrenCache[itemCheckbox.id] || []
  }

  /**
   * This functions serves to iterate through all the 'tree' of items
   * in order to figure out each inner/parent selection state based on the currently
   * selected/unselected items.
   *
   * It follows these rules defined at +resolveInnerItemSelectionState+.
   *
   * In order to navigate all the tree, we apply those rules recursively.
   */
  resolveSelectionStateForInnerItems() {
    // Always begin the iteration at the top of the tree
    this.topLevelItems.forEach((item) => {
      this.resolveInnerItemSelectionState(new CheckboxItem(item))
    })
  }

  /**
   * Resolves the selection state of the inner/parent item based on these rules:
   *
   * - The inner/parent check box is checked when all children are selected
   * - The inner/parent check box is unchecked when all children are deselected
   * - If some but not all children are selected, the parent/inner box has a dash
   */
  resolveInnerItemSelectionState(itemCheckbox) {
    const children = this.childrenCache[itemCheckbox.id]
    const isInnerItem = children.length > 0

    if (!isInnerItem) return

    const checkedChildren = []
    const uncheckedChildren = []
    const partiallyCheckedChildren = []

    children.forEach((child) => {
      const childCheckbox = new CheckboxItem(child)

      this.resolveInnerItemSelectionState(childCheckbox)

      if (childCheckbox.isPartiallyChecked) {
        partiallyCheckedChildren.push(childCheckbox)
      } else if (childCheckbox.isChecked) {
        checkedChildren.push(childCheckbox)
      } else {
        uncheckedChildren.push(childCheckbox)
      }
    })

    const fullyChecked = checkedChildren.length === children.length
    const fullyUnchecked = uncheckedChildren.length === children.length
    const partiallyChecked = partiallyCheckedChildren.length > 0 || (checkedChildren.length > 0 && !fullyChecked)

    if (fullyChecked) itemCheckbox.check()
    if (fullyUnchecked) itemCheckbox.uncheck()
    if (partiallyChecked) itemCheckbox.checkPartially()
  }

  innerItemToggled(ev) {
    ev.preventDefault()

    const itemCheckbox = new CheckboxItem(ev.currentTarget)

    itemCheckbox.assureCorrectStateAfterChangeEvent()

    // Toggle all checkboxes downstream when a parent/inner checkbox is clicked. Ie, if the
    // parent/inner checkbox is checked, all children should be checked. If unchecked, all
    // children should be unchecked.
    itemCheckbox.childrenAndGrandchildren.forEach((child) => {
      const childCheckbox = new CheckboxItem(child)
      itemCheckbox.isChecked ? childCheckbox.check() : childCheckbox.uncheck()
    })

    this.resolveSelectionStateForInnerItems()
  }

  leafItemToggled(ev) {
    ev.preventDefault()

    const itemCheckbox = new CheckboxItem(ev.currentTarget)

    itemCheckbox.assureCorrectStateAfterChangeEvent()

    this.resolveSelectionStateForInnerItems()
  }
}

class CheckboxItem {
  constructor(item) {
    this.item = item
  }

  get id() {
    return this.item.id
  }

  get childrenAndGrandchildren() {
    const listGroup = this.item.closest('.list-group')

    return listGroup ? listGroup.querySelectorAll('input[type="checkbox"]') : []
  }

  get isUnchecked() {
    return !this.isChecked
  }

  get isChecked() {
    return this.item.checked
  }

  get isPartiallyChecked() {
    return this.item.classList.contains(this._partiallyCheckedCssClass)
  }

  assureCorrectStateAfterChangeEvent() {
    if (this.isPartiallyChecked) {
      // When the item was partially checked and is toggled, it must navigate to the 'checked' state
      this.check()
    } else if (this.isChecked) {
      this.check()
    } else {
      this.uncheck()
    }
  }

  check(item) {
    this.item.checked = true
    this.item.classList.remove(this._partiallyCheckedCssClass)
  }

  checkPartially(item) {
    this.item.checked = false
    this.item.classList.add(this._partiallyCheckedCssClass)
  }

  uncheck(item) {
    this.item.checked = false
    this.item.classList.remove(this._partiallyCheckedCssClass)
  }

  get _partiallyCheckedCssClass() {
    return 'c-checkbox__input--partially-checked'
  }
}
